// Baking a chart for the HTML export (TODO §15/§9.2).
//
// === Why VECTOR here, and raster for a gantt and a grid — §12's first open decision =========
//
// §9.2 offers two options and recommends the first; this is that decision taken in code, with
// the reason written down as §12.1 asks:
//
//   **(a) Inline `<svg>`, serialized with `XMLSerializer`.** A Recharts chart already IS an
//   SVG in our own React tree. Serializing it costs a string, the result is a few KB rather
//   than a few hundred, and it stays crisp at any zoom — which matters, because the exported
//   file is opened full-screen on displays we do not choose.
//
//   **(b) A raster PNG** via `modern-screenshot`, the way a grid and a gantt are baked.
//
// (a) wins, and the risk §9.2 names is real but does not apply: text in the SVG falls back if
// the viewer lacks the stylesheet's font. The export already ships **text blocks** as HTML
// with exactly the same exposure, so this is consistent with the file's existing behaviour
// rather than a new risk it introduces. (b) becomes the right answer only if a chart ever
// renders HTML inside a `<foreignObject>` — which it will not, with tooltips off (§8.4).
//
// A grid is baked as a raster because it is a CROSS-ORIGIN IFRAME and there is no DOM here to
// serialize; a gantt because it is a React widget plus 150KB of vendor CSS. Neither reason is
// true of a chart. The asymmetry is the point, not an inconsistency.
//
// Two rules inherited from `collectGanttImages`, and both matter:
//   • it runs off the STORE's bodies, never the on-screen component, so an export does not
//     depend on a slide having been scrolled into view;
//   • one unrenderable chart costs that window, not the export.
//
// §9.4 And the rule that governs all of it: **no fetch on the export path.** This function
// renders the CACHED rows and nothing else — an exported deck is a document, not a dashboard.
// It is enforced structurally rather than by a flag: `ChartCanvas` has no fetch in it, and the
// refresh timers live in `ChartBlock`, which is not what this mounts.

import { createRoot } from "react-dom/client";

import { loadChartCanvas } from "@/lib/chart-engine";
import { resolveChartStyle } from "@/lib/stylesheet-chart";
import { sheetSwatches } from "@/lib/stylesheet-typography";
import type { ChartDocument } from "@/types/chart";
import type { GriddStyleSheetData, SlideData } from "@/types/document";

// The box each chart is authored into for the bake. An APPROXIMATION of the content window's
// size inside the exported 1600×900 canvas (`.content` is flex:1 under the title rows,
// `.frame` takes a 12px gap between two windows), and it is allowed to be: the SVG is fitted
// into whatever the frame turns out to be, and a chart re-fits to the box it is given. The
// same numbers `ganttImage.tsx` uses, for the same reason.
const WINDOW_W = 1560;
const WINDOW_H = 600;
const WINDOW_GAP = 12;

function windowBox(
  slide: SlideData,
  index: number,
): { width: number; height: number } {
  const ratio = slide.splitRatio ?? 0.5;
  const share = index === 0 ? ratio : 1 - ratio;
  if (slide.layout === "two-h") {
    return { width: (WINDOW_W - WINDOW_GAP) * share, height: WINDOW_H };
  }
  if (slide.layout === "two-v") {
    return { width: WINDOW_W, height: (WINDOW_H - WINDOW_GAP) * share };
  }
  return { width: WINDOW_W, height: WINDOW_H };
}

// §9.3 Let the tree mount and lay out before the serializer reads it. Two animation frames
// plus a tick — the same wait the thumbnail capturer and the gantt bake rely on. Without it
// the SVG is captured before its first layout pass and the file holds an empty box, which
// nothing would ever invalidate.
//
// It is also why `readOnly` is passed below: with animation on, one frame after mount is a
// chart drawn at 0% of its height (§8.5).
function settle(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setTimeout(resolve, 0)),
    );
  });
}

async function bakeOne(
  doc: ChartDocument,
  box: { width: number; height: number },
  styleSheet: GriddStyleSheetData,
): Promise<string | null> {
  const { default: ChartCanvas } = await loadChartCanvas();
  const host = document.createElement("div");
  // Off-screen but IN the document: an element in no document has no layout, so the chart
  // would measure zero and render nothing. Fixed rather than absolute so it cannot extend the
  // page's scroll area while it is there.
  host.style.cssText = `position:fixed;left:-100000px;top:0;width:${box.width}px;height:${box.height}px;`;
  document.body.appendChild(host);
  const root = createRoot(host);
  try {
    root.render(
      <ChartCanvas
        doc={doc}
        box={box}
        // §8.5/§8.4/§11.2 all at once: no animation to catch mid-frame, no tooltip markup in
        // the output, and no key bindings in a file that has no React to run them.
        readOnly
        style={resolveChartStyle(styleSheet)}
        swatches={sheetSwatches(styleSheet)}
      />,
    );
    await settle();
    const svg = host.querySelector("svg");
    if (!svg) return null;
    // A serialized fragment carries no namespace unless the element does, and a bare `<svg>`
    // pasted into an HTML document without `xmlns` renders as nothing at all. Recharts sets it
    // on its root, but setting it here means this does not depend on that staying true.
    svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    // Explicit dimensions plus a viewBox, so the exported frame can scale it with CSS: without
    // the viewBox an SVG with fixed width/height simply clips instead of fitting.
    svg.setAttribute("viewBox", `0 0 ${box.width} ${box.height}`);
    svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
    return new XMLSerializer().serializeToString(svg);
  } catch (err) {
    console.warn("[htmlExport] chart bake failed", err);
    return null;
  } finally {
    // Unmount out of band: React refuses to unmount a root synchronously from inside a render
    // pass, and this can be called from one.
    setTimeout(() => {
      root.unmount();
      host.remove();
    }, 0);
  }
}

// chartDocId → an SVG string. Absent entries render an empty frame, exactly as a missing grid
// snapshot does.
export async function collectChartSvgs(
  slides: SlideData[],
  docs: Record<string, ChartDocument>,
  styleSheet: GriddStyleSheetData,
): Promise<Record<string, string>> {
  const targets = new Map<string, { width: number; height: number }>();
  for (const slide of slides) {
    slide.contents.forEach((content, index) => {
      if (content?.kind === "chart" && !targets.has(content.chartDocId)) {
        targets.set(content.chartDocId, windowBox(slide, index));
      }
    });
  }
  const out: Record<string, string> = {};
  // Sequential, like the gantt pass: each bake puts a live React tree in the document and
  // measures it, and several at once would contend for layout with each other.
  for (const [docId, box] of targets) {
    const doc = docs[docId];
    if (!doc) continue;
    const svg = await bakeOne(doc, box, styleSheet);
    if (svg) out[docId] = svg;
  }
  return out;
}
